import dayjs from "dayjs";

import ActionButtons from "@/pages/Dashboard/components/ActionButtons";
import { formatTime } from "@/pages/Dashboard/shared";
import type { ReservationByState } from "@/types/reservation";
import type { UserByState } from "@/types/user";
import type { VehicleReservation } from "@/types/vehicleReservation";

interface AdminPendingReservationsProps {
  reservations: ReservationByState[];
  onViewAll: () => void;
  onApprove: (rsvtId: number, plcNm: string) => void;
  onReject: (rsvtId: number, plcNm: string) => void;
}

export const AdminPendingReservations = ({
  reservations,
  onViewAll,
  onApprove,
  onReject,
}: AdminPendingReservationsProps) => (
  <div className="rounded-xl border border-gray-200 bg-white shadow-sm">
    <div className="flex items-center justify-between border-b border-gray-100 px-5 py-3">
      <h2 className="text-sm font-bold text-gray-800">장소 예약 승인 대기</h2>
      {reservations.length > 0 && (
        <button
          type="button"
          onClick={onViewAll}
          className="text-xs text-primary hover:underline"
        >
          전체 보기
        </button>
      )}
    </div>
    <div className="divide-y divide-gray-50">
      {reservations.length === 0 ? (
        <div className="px-5 py-8 text-center text-sm text-gray-400">
          대기 중인 예약이 없습니다
        </div>
      ) : (
        reservations.slice(0, 5).map(r => (
          <div
            key={r.rsvtId}
            className="flex items-center justify-between gap-3 px-5 py-3 transition-colors hover:bg-gray-50"
          >
            <div className="flex min-w-0 flex-col">
              <div className="flex items-center gap-2">
                <span className="text-sm font-medium text-gray-800">
                  {r.plcNm}
                </span>
                <span className="text-xs text-gray-400">
                  {formatTime(r.startTime)}~{formatTime(r.endTime)}
                </span>
              </div>
              <span className="text-xs text-gray-400">
                {dayjs(r.rsvtDt, "YYYYMMDD").format("M/D (dd)")} · {r.userName}{" "}
                · {r.useCnts}
              </span>
            </div>
            <ActionButtons
              onApprove={() => onApprove(r.rsvtId, r.plcNm)}
              onReject={() => onReject(r.rsvtId, r.plcNm)}
            />
          </div>
        ))
      )}
      {reservations.length > 5 && (
        <div className="px-5 py-2 text-center text-xs text-gray-400">
          외 {reservations.length - 5}건
        </div>
      )}
    </div>
  </div>
);

interface AdminPendingVehiclesProps {
  vehicles: VehicleReservation[];
  onViewAll: () => void;
  onApprove: (id: number, destination: string) => void;
  onReject: (id: number, destination: string) => void;
}

export const AdminPendingVehicles = ({
  vehicles,
  onViewAll,
  onApprove,
  onReject,
}: AdminPendingVehiclesProps) => (
  <div className="rounded-xl border border-gray-200 bg-white shadow-sm">
    <div className="flex items-center justify-between border-b border-gray-100 px-5 py-3">
      <h2 className="text-sm font-bold text-gray-800">차량 예약 승인 대기</h2>
      {vehicles.length > 0 && (
        <button
          type="button"
          onClick={onViewAll}
          className="text-xs text-primary hover:underline"
        >
          전체 보기
        </button>
      )}
    </div>
    <div className="divide-y divide-gray-50">
      {vehicles.length === 0 ? (
        <div className="px-5 py-8 text-center text-sm text-gray-400">
          대기 중인 차량 예약이 없습니다
        </div>
      ) : (
        vehicles.slice(0, 5).map(v => (
          <div
            key={v.id}
            className="flex items-center justify-between gap-3 px-5 py-3 transition-colors hover:bg-gray-50"
          >
            <div className="flex min-w-0 flex-col">
              <div className="flex items-center gap-2">
                <span className="text-sm font-medium text-gray-800">
                  {v.destination}
                </span>
                <span className="text-xs text-gray-400">{v.drivingTime}</span>
              </div>
              <span className="text-xs text-gray-400">
                {dayjs(v.reservationDate).format("M/D (dd)")} ·{" "}
                {v.applicantName} · {v.purpose}
              </span>
            </div>
            <ActionButtons
              onApprove={() => onApprove(v.id, v.destination)}
              onReject={() => onReject(v.id, v.destination)}
            />
          </div>
        ))
      )}
      {vehicles.length > 5 && (
        <div className="px-5 py-2 text-center text-xs text-gray-400">
          외 {vehicles.length - 5}건
        </div>
      )}
    </div>
  </div>
);

interface AdminPendingUsersProps {
  users: UserByState[];
  onViewAll: () => void;
  onApprove: (userId: string, userName: string) => void;
  onReject: (userId: string, userName: string) => void;
}

export const AdminPendingUsers = ({
  users,
  onViewAll,
  onApprove,
  onReject,
}: AdminPendingUsersProps) => (
  <div className="rounded-xl border border-gray-200 bg-white shadow-sm">
    <div className="flex items-center justify-between border-b border-gray-100 px-5 py-3">
      <h2 className="text-sm font-bold text-gray-800">회원가입 승인 대기</h2>
      {users.length > 0 && (
        <button
          type="button"
          onClick={onViewAll}
          className="text-xs text-primary hover:underline"
        >
          전체 보기
        </button>
      )}
    </div>
    <div className="divide-y divide-gray-50">
      {users.length === 0 ? (
        <div className="px-5 py-8 text-center text-sm text-gray-400">
          대기 중인 회원이 없습니다
        </div>
      ) : (
        users.slice(0, 5).map(u => (
          <div
            key={u.userId}
            className="flex items-center justify-between px-5 py-3 transition-colors hover:bg-gray-50"
          >
            <div>
              <span className="text-sm font-medium text-gray-800">
                {u.userName}
              </span>
              <span className="ml-2 text-xs text-gray-400">{u.telNo}</span>
            </div>
            <ActionButtons
              onApprove={() => onApprove(u.userId, u.userName)}
              onReject={() => onReject(u.userId, u.userName)}
            />
          </div>
        ))
      )}
      {users.length > 5 && (
        <div className="px-5 py-2 text-center text-xs text-gray-400">
          외 {users.length - 5}명
        </div>
      )}
    </div>
  </div>
);

interface AdminTodayReservationsProps {
  reservations: ReservationByState[];
  onViewCalendar: () => void;
}

export const AdminTodayReservations = ({
  reservations,
  onViewCalendar,
}: AdminTodayReservationsProps) => (
  <div className="rounded-xl border border-gray-200 bg-white shadow-sm">
    <div className="flex items-center justify-between border-b border-gray-100 px-5 py-3">
      <h2 className="text-sm font-bold text-gray-800">오늘의 예약 현황</h2>
      <button
        type="button"
        onClick={onViewCalendar}
        className="text-xs text-primary hover:underline"
      >
        캘린더 보기
      </button>
    </div>
    <div className="divide-y divide-gray-50">
      {reservations.length === 0 ? (
        <div className="px-5 py-8 text-center text-sm text-gray-400">
          오늘 예약이 없습니다
        </div>
      ) : (
        reservations.slice(0, 5).map(r => (
          <div
            key={r.rsvtId}
            className="flex items-center justify-between px-5 py-3 transition-colors hover:bg-gray-50"
          >
            <div className="flex flex-col">
              <div className="flex items-center gap-2">
                <span className="text-sm font-medium text-gray-800">
                  {r.plcNm}
                </span>
                <span className="text-xs text-gray-400">
                  {formatTime(r.startTime)}~{formatTime(r.endTime)}
                </span>
              </div>
              <span className="text-xs text-gray-400">
                {r.userName} · {r.useCnts}
              </span>
            </div>
            <span className="rounded-full bg-primary/10 px-2 py-0.5 text-[10px] font-medium text-primary">
              진행
            </span>
          </div>
        ))
      )}
    </div>
  </div>
);
